/** Accept serialized WhatsApp BODY text, never HTML or visible variable names. */
export function normalizeTemplateBody(body) {
  return typeof body === "string" ? body.replace(/\r\n?/g, "\n").trim() : "";
}

/**
 * Distinct variable numbers used by a serialized body (`{{1}}`, `{{2}}`…),
 * sorted ascending. The same variable repeated counts once.
 */
export function getTemplateBodyVariables(body) {
  if (typeof body !== "string") return [];
  const numbers = (body.match(/\{\{\d+\}\}/g) || []).map((placeholder) =>
    Number(placeholder.slice(2, -2)),
  );
  return Array.from(new Set(numbers)).sort((a, b) => a - b);
}

// Meta does not count whitespace, `. ! ?` or the bold/italic markers `* _` as
// text when it decides whether a body starts or ends with a variable. Verified
// against the provider on 2026-10-13; other characters (`¡`, `)`, emoji) do
// count as text, so only these are stripped.
const EDGE_NOISE = /^[\s.!?*_]+|[\s.!?*_]+$/g;

const EDGE_HINT =
  "Los signos . ! ? y el formato de negritas o cursivas (* y _) no cuentan como texto";

// A `{{…}}` token typed as plain text. It must be searched in the editor's
// VISIBLE text (`normalize().text`): there every field created by the system
// shows as its example word, so only braces the user typed remain. In the wire
// text (`normalize().markdown`) a field is `{{n}}` too, and a typed `{{1}}`
// next to a real field 1 cannot be told apart from it.
const HAND_TYPED_VARIABLE = /\{\{[^{}\n]*\}\}/g;

export const HAND_TYPED_VARIABLES_MESSAGES = {
  // Settings, template with custom fields.
  fields:
    "No escribas variables con llaves, como {{1}}. Escribe el mensaje con datos de ejemplo (por ejemplo, «Hola Ana») y pulsa «Continuar»: el sistema detecta los campos personalizables y tú eliges cuáles cambian por cliente. La palabra que elijas es el ejemplo que se envía a WhatsApp.",
  // Settings, simple template: it has no variables at all. The card title in
  // the text is the one the user clicked («customWhatsappTemplateCardTitle»).
  simple:
    "Una plantilla simple no lleva variables. Si necesitas variables, regresa y elige «Plantilla con campos personalizables»; si no, quita las llaves ({{1}}).",
  // Campaigns: the author creates the fields by hand with «Crear campo».
  campaign:
    "No escribas variables con llaves, como {{1}}. Escribe el mensaje con datos de ejemplo, selecciona la palabra que cambia por cliente y usa «Crear campo»: esa palabra es el ejemplo que se envía a WhatsApp.",
};

/** Every `{{…}}` token typed as text in the editor's visible text. */
export function findHandTypedVariables(visibleText) {
  if (typeof visibleText !== "string") return [];
  return visibleText.match(HAND_TYPED_VARIABLE) || [];
}

/**
 * Blocks braces typed by hand. `context` picks the message: `fields`
 * (Settings, custom fields), `simple` (Settings, no fields) or `campaign`.
 */
export function validateNoHandTypedVariables(visibleText, context = "fields") {
  const tokens = findHandTypedVariables(visibleText);
  if (!tokens.length) return { valid: true, code: null, message: "", tokens };
  return {
    valid: false,
    code: "hand-typed-variable",
    message:
      HAND_TYPED_VARIABLES_MESSAGES[context] ||
      HAND_TYPED_VARIABLES_MESSAGES.fields,
    tokens,
  };
}

export function isHandTypedVariablesMessage(message) {
  return Object.values(HAND_TYPED_VARIABLES_MESSAGES).includes(message);
}

export function validateTemplateBody(body) {
  const normalized = normalizeTemplateBody(body);
  const edges = normalized.replace(EDGE_NOISE, "");
  const variables = getTemplateBodyVariables(normalized);
  const rules = [
    [!normalized, "required", "El cuerpo del mensaje es requerido"],
    [
      normalized.length < 10,
      "min",
      "El cuerpo del mensaje debe tener al menos 10 caracteres",
    ],
    [
      normalized.length > 1024,
      "max",
      "El cuerpo del mensaje debe tener menos de 1024 caracteres",
    ],
    [
      /\n{3,}/.test(normalized),
      "line-breaks",
      "El mensaje no puede tener más de 2 saltos de línea consecutivos",
    ],
    [
      /^\{\{\d+\}\}/.test(edges),
      "start-variable",
      `El mensaje no puede iniciar con un campo. ${EDGE_HINT}: escribe una palabra antes del campo.`,
    ],
    [
      /\{\{\d+\}\}$/.test(edges),
      "end-variable",
      `El mensaje no puede terminar con un campo. ${EDGE_HINT}: escribe una palabra después del campo.`,
    ],
    [
      variables.some((number, index) => number !== index + 1),
      "variable-sequence",
      "Las variables deben ir numeradas en orden y sin saltos: {{1}}, {{2}}, {{3}}…",
    ],
  ];
  const failure = rules.find(([invalid]) => invalid);
  return {
    valid: !failure,
    normalized,
    code: failure ? failure[1] : null,
    message: failure ? failure[2] : "",
  };
}

/**
 * Meta rejects a template whose variables have no example (or a blank one).
 * `examples[n - 1]` is the example of `{{n}}`; a variable repeated in the body
 * needs a single example. A body without variables is always valid.
 */
export function validateTemplateExamples(body, examples) {
  const count = getTemplateBodyVariables(body).length;
  const complete = Array.from({ length: count }).every(
    (_, index) =>
      Array.isArray(examples) &&
      typeof examples[index] === "string" &&
      examples[index].trim() !== "",
  );
  return complete
    ? { valid: true, code: null, message: "" }
    : {
        valid: false,
        code: "examples-missing",
        message:
          'Falta el ejemplo de una o más variables. Para agregar una variable, selecciona en el mensaje el texto de ejemplo y usa "Crear campo"; una variable escrita a mano, como {{1}}, no tiene ejemplo.',
      };
}
